import React, { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import {
  GENERATE_REGISTRATION_OPTIONS,
  ENROLL_WEBAUTHN,
} from "../../graphql/mutations/webauthn";
import Buttons from "../Button/button.component";
import environment from "../../env";

type WebAuthnEnrollmentProps = {
  onSuccess: (credentialData: any) => void; // Callback function to handle success
};

function WebAuthnEnrollment({ onSuccess }: WebAuthnEnrollmentProps) {
  const [enrollmentStatus, setEnrollmentStatus] = useState(
    "Initializing enrollment..."
  );
  const [generateOptionsMutation] = useMutation(GENERATE_REGISTRATION_OPTIONS, {
    variables: { ...environment.paymentService },
    context: {
      credentials: "include", // Include cookies in the request
    },
    fetchPolicy: "no-cache",
  });
  const [enrollWebAuthnMutation] = useMutation(ENROLL_WEBAUTHN, {
    variables: { ...environment.paymentService },
    context: {
      credentials: "include", // Include cookies in the request
    },
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    async function enrollWebAuthn() {
      try {
        // Step 1: Generate registration options
        setEnrollmentStatus("Generating registration options...");
        const { data: optionsData } = await generateOptionsMutation();

        const options = optionsData?.generateRegistrationOptions;
        if (!options) {
          throw new Error("Failed to generate registration options.");
        }

        // Decode Base64URL-encoded properties to ArrayBuffer
        const decodedOptions = {
          ...options,
          challenge: decodeBase64ToArrayBuffer(options.challenge),
          user: {
            ...options.user,
            id: decodeBase64ToArrayBuffer(options.user.id),
          },
        };

        // Step 2: Create WebAuthn credential
        setEnrollmentStatus("Creating WebAuthn credential...");
        const credential: any = await navigator.credentials.create({
          publicKey: decodedOptions,
        });

        if (!credential) {
          throw new Error("WebAuthn credential creation failed.");
        }

        // Convert response properties to base64url-encoded strings
        const credentialData = {
          id: credential.id,
          rawId: base64urlEncode(new Uint8Array(credential.rawId)),
          type: credential.type,
          response: {
            attestationObject: base64urlEncode(
              new Uint8Array(credential.response.attestationObject)
            ),
            clientDataJSON: base64urlEncode(
              new Uint8Array(credential.response.clientDataJSON)
            ),
          },
        };

        // Step 3: Send credential to backend for enrollment
        setEnrollmentStatus("Sending credential to backend...");
        const { data: enrollData } = await enrollWebAuthnMutation({
          variables: {
            credential: credentialData,
          },
        });

        if (enrollData?.enrollWebauthn?.success === true) {
          setEnrollmentStatus("WebAuthn enrollment successful!");
          onSuccess(credentialData); // Call the onSuccess callback
        } else if (enrollData?.enrollWebauthn?.success === false) {
          const errorMessage =
            enrollData?.enrollWebauthn?.message || "Enrollment failed.";
          throw new Error(errorMessage);
        } else {
          throw new Error("Unexpected response structure from the backend.");
        }
      } catch (error: any) {
        setEnrollmentStatus(`WebAuthn enrollment error: ${error.message}`);
      }
    }

    // Start the enrollment process on component load
    enrollWebAuthn();
  }, [generateOptionsMutation, enrollWebAuthnMutation, onSuccess]);

  // Helper function to decode Base64URL to ArrayBuffer
  function decodeBase64ToArrayBuffer(base64url: string): ArrayBuffer {
    const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  // Helper function to encode ArrayBuffer to base64url
  function base64urlEncode(buffer: Uint8Array): string {
    return btoa(String.fromCharCode(...Array.from(buffer)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  }

  return (
    <div>
      {/* <Buttons
         disabled={!isValidOTPs}
          style={{ width: "100%", marginTop: 15 }}
          label={buttonLabel || "Confirm OTP code"}
          width="83%"
          color={colors.ButtonBackgroundColors.primaryMediumHoverButton}
          cursor="pointer"
          border="15px"
          height="40px"
          handleClick={() => handleSubmit(otpValues, to)}
          /> */}
      <p>Status: {enrollmentStatus}</p>
    </div>
  );
}

export default WebAuthnEnrollment;
